import { ArrowUpRight, BookOpen } from 'lucide-react';
import { ObraCoverImage } from '@/components/obra/ObraCoverImage';

export interface MangaReferenceCardProps {
    title: string;
    cover: string;
    onClick?: () => void;
    /** `obra`: sem badge de capítulo; `capitulo`: badge CAP. */
    contextKind: 'obra' | 'capitulo';
    /** Exibido no badge só quando `contextKind === 'capitulo'` (número ou texto da API). */
    chapter?: number | string | null;
}

/**
 * Card de referência (mangá / capítulo) para exibir abaixo de um comentário no feed.
 */
export function MangaReferenceCard({
    title,
    cover,
    onClick,
    contextKind,
    chapter,
}: MangaReferenceCardProps) {
    const isObra = contextKind === 'obra';
    const chapterDisplay =
        chapter === null || chapter === undefined
            ? '?'
            : typeof chapter === 'number'
              ? String(chapter)
              : chapter;

    const hint = isObra ? 'Toque para abrir a obra' : 'Toque para abrir o capítulo';
    const ariaLabel = onClick
        ? isObra
            ? `Abrir obra ${title}`
            : `Abrir ${title}, capítulo ${chapterDisplay}`
        : undefined;

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!onClick}
            aria-label={ariaLabel}
            className={[
                'group relative w-full max-w-xl overflow-hidden rounded-xl border p-3 text-left',
                'bg-gradient-to-br from-neutral-950 via-black to-black',
                'border-white/10 shadow-md shadow-black/60',
                'transition-all duration-[350ms] ease-out',
                'hover:-translate-y-0.5 hover:border-orange-500/40 hover:bg-white/[0.03] hover:shadow-lg hover:shadow-orange-500/20',
                'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-black',
                'focus-visible:outline-none',
                'disabled:pointer-events-none disabled:opacity-60',
            ].join(' ')}
        >
            <span
                className="pointer-events-none absolute -right-6 -top-10 size-36 rounded-full bg-orange-500/15 blur-3xl transition-all duration-[350ms] ease-out group-hover:bg-orange-500/35 group-hover:opacity-100"
                aria-hidden
            />

            <span className="relative flex w-full flex-row items-center gap-3 sm:gap-4">
                <div className="relative h-20 w-16 shrink-0 overflow-hidden rounded-lg ring-1 ring-white/10 transition-transform duration-[350ms] ease-out group-hover:ring-orange-500/30">
                    <ObraCoverImage
                        src={cover}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="size-full object-cover transition-transform duration-[350ms] ease-out group-hover:scale-110"
                        iconClassName="size-7 text-white/30"
                    />
                    <div
                        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent"
                        aria-hidden
                    />
                </div>

                <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-orange-600 to-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm shadow-black/40">
                            <BookOpen className="size-3 shrink-0" aria-hidden />
                            {isObra ? 'OBRA' : `CAP. ${chapterDisplay}`}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/45">
                            MENCIONADO EM
                        </span>
                    </div>
                    <p className="truncate text-sm font-bold uppercase tracking-wide text-white transition-colors duration-[350ms] ease-out group-hover:text-orange-400">
                        {title}
                    </p>
                    <p className="mt-0.5 text-xs text-white/50">{hint}</p>
                </div>

                <div
                    className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-[350ms] ease-out group-hover:rotate-12 group-hover:bg-orange-500 group-hover:text-white"
                    aria-hidden
                >
                    <ArrowUpRight className="size-[18px]" strokeWidth={2.25} />
                </div>
            </span>
        </button>
    );
}
